import React, { useState, useCallback, useEffect } from 'react';
import {
  ArrowPathIcon,
  XCircleIcon,
  ClockIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { getCashFlowDaily } from '../api/client';
import { useStores } from '../contexts/StoreContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function localDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function fmt(val, currency) {
  if (val === null || val === undefined) return '—';
  const n = Number(val);
  const s = Math.abs(n).toLocaleString('es-VE', {
    minimumFractionDigits: currency === 'usd' ? 0 : 2,
    maximumFractionDigits: currency === 'usd' ? 0 : 2,
  });
  return `${n < 0 ? '−' : ''}${currency === 'usd' ? '$' : 'Bs '}${s}`;
}

// Montos de detalle vienen en Bs; en modo USD se convierten con la tasa de la tienda ese día
function conv(ves, rate, currency) {
  if (currency === 'ves') return ves;
  return rate ? ves / rate : 0;
}

function fmtDay(iso) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('es-VE', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

function fmtShort(iso) {
  return iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—';
}

// ─── Columnas del resumen ────────────────────────────────────────────────────

const BUCKETS = [
  { key: 'en_banco',          label: 'Ya en banco',        bar: 'bg-green-500',  text: 'text-green-700' },
  { key: 'en_transito',       label: 'Cerrado tarde',      bar: 'bg-sky-500',    text: 'text-sky-700' },
  { key: 'cashea_por_cobrar', label: 'Cashea por cobrar',  bar: 'bg-purple-500', text: 'text-purple-700' },
  { key: 'comisiones',        label: 'Comisiones',         bar: 'bg-gray-400',   text: 'text-gray-600' },
  { key: 'efectivo_divisas',  label: 'Efectivo y divisas', bar: 'bg-amber-400',  text: 'text-amber-700', info: true },
  { key: 'credito_clientes',  label: 'Crédito clientes',   bar: 'bg-amber-200',  text: 'text-amber-600', info: true },
  { key: 'por_aclarar',       label: 'Por aclarar',        bar: 'bg-red-500',    text: 'text-red-700' },
];

function porAclararClass(v, sales) {
  const pct = sales ? Math.abs(v) / sales : 0;
  if (pct < 0.02) return 'text-gray-400';
  return v > 0 ? 'text-red-600 font-semibold' : 'text-orange-600 font-semibold';
}

function CompositionBar({ values }) {
  const sales = values.sales || 0;
  if (sales <= 0) return null;
  return (
    <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-gray-100 mt-1">
      {BUCKETS.map(b => {
        const v = Math.max(values[b.key] || 0, 0);
        const w = Math.min(v / sales * 100, 100);
        return w > 0 ? <div key={b.key} className={b.bar} style={{ width: `${w}%` }} title={`${b.label}: ${Math.round(w)}%`} /> : null;
      })}
    </div>
  );
}

function SummaryCard({ label, value, sub, colorClass }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${colorClass}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-0.5">{label}</p>
      <p className="text-xl font-black">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Detalle de una tienda en un día ─────────────────────────────────────────

const LOTE_STATE = {
  a_tiempo:   { label: 'Cae al día siguiente', cls: 'bg-green-100 text-green-800' },
  tarde:      { label: 'Cae un día después',   cls: 'bg-sky-100 text-sky-800' },
  sospechoso: { label: 'Monto dudoso · no cuenta', cls: 'bg-orange-100 text-orange-800' },
  duplicado:  { label: 'Repetido · no cuenta',     cls: 'bg-orange-100 text-orange-800' },
};

function StoreDetail({ row, currency, cutoffHour }) {
  const rate = row.exchange_rate;
  const m = v => fmt(conv(v, rate, currency), currency);
  const banks = row.pos.banks || [];
  const det = row.por_aclarar_detalle || {};
  const cutoffLabel = `${cutoffHour > 12 ? cutoffHour - 12 : cutoffHour}pm`;

  return (
    <tr className="bg-gray-50/70">
      <td colSpan={BUCKETS.length + 2} className="px-6 py-4">
        <div className="space-y-4 max-w-6xl">
          {/* POS por banco */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
              Tarjetas (POS) por banco · vendido {m(row.pos.sold_ves)}
            </p>
            <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="text-gray-400 uppercase tracking-wide border-b border-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Banco</th>
                    <th className="px-3 py-2 text-right">Vendido</th>
                    <th className="px-3 py-2 text-right">Lotes antes de las {cutoffLabel}</th>
                    <th className="px-3 py-2 text-right">Lotes desde las {cutoffLabel}</th>
                    <th className="px-3 py-2 text-right" title="Vendido con tarjeta que no aparece en ningún lote cerrado">Vendido sin lote</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {banks.map(b => (
                    <tr key={b.banco}>
                      <td className="px-3 py-2 font-semibold">
                        {b.banco_display}
                        {b.invalid_count > 0 && (
                          <span className="ml-1 bg-orange-100 text-orange-700 px-1 rounded" title="Lotes con monto imposible o repetidos: no se cuentan">⚠ {b.invalid_count}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{m(b.sold_ves)}</td>
                      <td className="px-3 py-2 text-right font-mono text-green-700">
                        {b.on_time_count ? <>{m(b.on_time_ves)} <span className="text-gray-400">({b.on_time_count})</span></> : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-sky-700">
                        {b.late_count ? <>{m(b.late_ves)} <span className="text-gray-400">({b.late_count})</span></> : '—'}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${porAclararClass(b.sin_lote_ves, b.sold_ves)}`}>{m(b.sin_lote_ves)}</td>
                    </tr>
                  ))}
                  {banks.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-3 text-center text-gray-400">Sin ventas por tarjeta</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Lotes uno a uno */}
          {banks.some(b => b.lotes.length) && (
            <details className="bg-white border border-gray-200 rounded-lg">
              <summary className="px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer">
                Ver lotes uno por uno ({banks.reduce((n, b) => n + b.lotes.length, 0)})
              </summary>
              <table className="w-full text-xs">
                <thead className="text-gray-400 uppercase tracking-wide border-y border-gray-100">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Banco</th>
                    <th className="px-3 py-1.5 text-left">Cierre</th>
                    <th className="px-3 py-1.5 text-left">Nº lote</th>
                    <th className="px-3 py-1.5 text-right">Monto</th>
                    <th className="px-3 py-1.5 text-left">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {banks.flatMap(b => b.lotes.map((l, i) => {
                    const st = LOTE_STATE[l.state] || LOTE_STATE.sospechoso;
                    return (
                      <tr key={`${b.banco}-${i}`}>
                        <td className="px-3 py-1.5">{b.banco_display}</td>
                        <td className={`px-3 py-1.5 font-mono ${l.late ? 'text-amber-700 font-semibold' : ''}`}>
                          {l.close_date !== row.date ? `${fmtShort(l.close_date)} ` : ''}{l.hora}
                          {l.late && <ClockIcon className="w-3 h-3 inline ml-1" title={`Cerrado después de las ${cutoffHour}:00`} />}
                        </td>
                        <td className="px-3 py-1.5 font-mono">{l.numerolote || '—'}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{m(l.amount_ves)}</td>
                        <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded ${st.cls}`}>{st.label}</span></td>
                      </tr>
                    );
                  }))}
                </tbody>
              </table>
            </details>
          )}

          {/* Otros métodos */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
            <div className="bg-white border border-purple-200 rounded-lg px-3 py-2">
              <p className="font-bold text-purple-800 mb-1">Cashea · {row.cashea.invoice_count} facturas</p>
              <p>Financiado: <span className="font-mono">{m(row.cashea.ves)}</span></p>
              <p>Comisión: <span className="font-mono">{m(row.cashea.commission_ves)}</span></p>
              <p>Cobrado: <span className="font-mono text-green-700">{m(row.cashea.cobrado_ves)}</span></p>
              <p>Por cobrar: <span className="font-mono text-purple-700 font-semibold">{m(row.cashea.por_cobrar_ves)}</span></p>
              <p className="text-gray-400 mt-1">Cashea paga el miércoles {fmtShort(row.cashea.payment_date)}</p>
            </div>
            <div className="bg-white border border-green-200 rounded-lg px-3 py-2">
              <p className="font-bold text-green-800 mb-1">Pago móvil</p>
              <p>Vendido: <span className="font-mono">{m(row.pago_movil.sold_ves)}</span></p>
              <p className="text-gray-400 mt-1">Se cuenta como ya en banco (cae el mismo día)</p>
            </div>
            <div className="bg-white border border-green-200 rounded-lg px-3 py-2">
              <p className="font-bold text-green-800 mb-1">Biopago (BDV)</p>
              <p>Vendido: <span className="font-mono">{m(row.biopago.sold_ves)}</span></p>
              <p className="text-gray-400 mt-1">Se cuenta como ya en banco</p>
            </div>
            <div className="bg-white border border-amber-200 rounded-lg px-3 py-2">
              <p className="font-bold text-amber-800 mb-1">Efectivo y divisas (no va al banco)</p>
              <p>Efectivo Bs: <span className="font-mono">{fmt(row.efectivo.cash_ves, 'ves')}</span></p>
              <p>Efectivo USD: <span className="font-mono">{fmt(row.efectivo.cash_usd, 'usd')}</span></p>
              <p>Zelle / TRF USD: <span className="font-mono">{fmt(row.efectivo.zelle_usd, 'usd')}</span></p>
            </div>
          </div>

          {/* Desglose de por aclarar */}
          <p className="text-xs text-gray-500">
            <strong>Por aclarar {m(row.ves.por_aclarar)}:</strong>{' '}
            tarjetas vendidas sin lote cerrado {m(det.pos_sin_lote_ves)} · métodos de pago sin clasificar {m(det.otros_ves)}.
          </p>
        </div>
      </td>
    </tr>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export default function CashInTransit() {
  const { stores } = useStores();
  const [dateFrom, setDateFrom] = useState(localDate(-7));
  const [dateTo,   setDateTo]   = useState(localDate(-1));
  const [storeId,  setStoreId]  = useState('');
  const [currency, setCurrency] = useState('usd');
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [openDays,   setOpenDays]   = useState(() => new Set());
  const [openStores, setOpenStores] = useState(() => new Set());

  const toggle = (setter, key) => setter(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getCashFlowDaily({ dateFrom, dateTo, storeId: storeId || null }));
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al cargar el reporte.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, storeId]);

  // Carga inicial con las fechas por defecto; después solo al presionar Consultar
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const totals = data?.totals?.[currency] || {};
  const days = data?.days || [];
  const lateTotal = days.reduce((acc, d) => ({
    count: acc.count + d.late_lotes.count,
    value: acc.value + d.late_lotes[currency],
  }), { count: 0, value: 0 });
  const cutoff = data?.cutoff_hour ?? 18;
  const cutoffLabel = `${cutoff > 12 ? cutoff - 12 : cutoff}:00 ${cutoff >= 12 ? 'pm' : 'am'}`;
  const pct = k => totals.sales ? `${Math.round((totals[k] || 0) / totals.sales * 100)}% de las ventas` : '';

  const headerCells = (
    <>
      <th className="px-3 py-2 text-right">Ventas</th>
      {BUCKETS.map(b => (
        <th key={b.key} className={`px-3 py-2 text-right ${b.info ? 'opacity-70' : ''}`}>{b.label}</th>
      ))}
    </>
  );

  const valueCells = (vals, late) => (
    <>
      <td className="px-3 py-2.5 text-right font-mono font-bold text-gray-900">{fmt(vals.sales, currency)}</td>
      {BUCKETS.map(b => (
        <td key={b.key} className={`px-3 py-2.5 text-right font-mono ${
          b.key === 'por_aclarar' ? porAclararClass(vals[b.key], vals.sales) : b.text
        } ${b.info ? 'opacity-70' : ''}`}>
          {Math.abs(vals[b.key] || 0) < (currency === 'usd' ? 0.5 : 1) ? <span className="text-gray-300">—</span> : fmt(vals[b.key], currency)}
          {b.key === 'en_transito' && late.count > 0 && <span className="text-xs opacity-60"> ({late.count})</span>}
        </td>
      ))}
    </>
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ArrowsRightLeftIcon className="w-7 h-7 text-indigo-600" />
          Dinero en Tránsito
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Dónde está lo que se vendió cada día: lo que ya cayó al banco, lo que viene en camino y lo que hay que revisar
        </p>
      </div>

      {/* Controles */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Desde</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Hasta</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Tienda</label>
          <select value={storeId} onChange={e => setStoreId(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
            <option value="">Todas las tiendas</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Moneda</label>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            {[['usd', 'USD'], ['ves', 'Bs']].map(([k, l]) => (
              <button key={k} onClick={() => setCurrency(k)}
                className={`px-3 py-2 font-semibold ${currency === k ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors">
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Calculando...' : 'Consultar'}
        </button>
      </div>

      {error && (
        <div className="mb-5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {loading && !data && (
        <div className="text-center py-16 text-gray-400 text-sm">Cruzando lotes, Cashea y estados de cuenta…</div>
      )}

      {data && (
        <>
          {/* Totales del período */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
            <SummaryCard label="Ventas" value={fmt(totals.sales, currency)} sub={`${days.length} días`}
              colorClass="bg-gray-50 border-gray-200 text-gray-900" />
            <SummaryCard label="Ya en banco" value={fmt(totals.en_banco, currency)} sub={pct('en_banco')}
              colorClass="bg-green-50 border-green-200 text-green-800" />
            <SummaryCard label="Cerrado tarde" value={fmt(totals.en_transito, currency)} sub={`lotes cerrados desde las ${cutoffLabel}: caen un día después`}
              colorClass="bg-sky-50 border-sky-200 text-sky-800" />
            <SummaryCard label="Cashea por cobrar" value={fmt(totals.cashea_por_cobrar, currency)} sub="neto de comisión"
              colorClass="bg-purple-50 border-purple-200 text-purple-800" />
            <SummaryCard label="Comisiones" value={fmt(totals.comisiones, currency)} sub="Cashea (4%)"
              colorClass="bg-gray-50 border-gray-200 text-gray-700" />
            <SummaryCard label="Efectivo y divisas" value={fmt(totals.efectivo_divisas, currency)}
              sub={`+ ${fmt(totals.credito_clientes, currency)} a crédito · solo referencia`}
              colorClass="bg-amber-50 border-amber-200 text-amber-800" />
            <SummaryCard label="Por aclarar" value={fmt(totals.por_aclarar, currency)} sub={pct('por_aclarar')}
              colorClass={Math.abs(totals.por_aclarar || 0) / (totals.sales || 1) < 0.02
                ? 'bg-gray-50 border-gray-200 text-gray-700'
                : 'bg-red-50 border-red-200 text-red-800'} />
          </div>

          {/* Lotes cerrados tarde */}
          {lateTotal.count > 0 && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <div className="flex items-start gap-3">
                <ClockIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-900">
                    <strong>{lateTotal.count} lotes cerrados después de las {cutoffLabel}</strong> por{' '}
                    <strong>{fmt(lateTotal.value, currency)}</strong>
                    {days.length > 0 && <> (≈ {fmt(lateTotal.value / days.length, currency)} por día)</>}.
                    {' '}Ese dinero cae al banco un día después.
                  </p>
                  {data.late_ranking.length > 1 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {data.late_ranking.map(s => (
                        <span key={s.store_id} className="text-xs bg-white border border-amber-200 rounded-full px-2.5 py-1 text-amber-900">
                          {s.store_name.replace(/ C\.?A\.?\s*$/i, '')}: <strong>{fmt(s[currency], currency)}</strong> · {s.count} lotes en {s.days} días
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tabla por día */}
          {days.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200 text-gray-500">
              No hay ventas registradas en ese período.
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Día de venta</th>
                    {headerCells}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {days.map(d => {
                    const dayOpen = openDays.has(d.date);
                    return (
                      <React.Fragment key={d.date}>
                        <tr onClick={() => toggle(setOpenDays, d.date)}
                          className={`cursor-pointer ${dayOpen ? 'bg-indigo-50/50' : 'hover:bg-gray-50'}`}>
                          <td className="px-3 py-2.5 min-w-[150px]">
                            <span className="flex items-center gap-2">
                              <span className={`text-gray-400 text-xs transition-transform ${dayOpen ? 'rotate-90' : ''}`}>▶</span>
                              <span className="font-semibold text-gray-800 capitalize">{fmtDay(d.date)}</span>
                            </span>
                            <CompositionBar values={d[currency]} />
                          </td>
                          {valueCells(d[currency], d.late_lotes)}
                        </tr>
                        {dayOpen && d.stores.map(s => {
                          const sk = `${d.date}|${s.store_id}`;
                          const storeOpen = openStores.has(sk);
                          return (
                            <React.Fragment key={sk}>
                              <tr onClick={() => toggle(setOpenStores, sk)}
                                className={`cursor-pointer text-xs ${storeOpen ? 'bg-indigo-50/30' : 'bg-gray-50/40 hover:bg-gray-50'}`}>
                                <td className="pl-9 pr-3 py-2">
                                  <span className="flex items-center gap-1.5">
                                    <span className={`text-gray-400 text-[10px] transition-transform ${storeOpen ? 'rotate-90' : ''}`}>▶</span>
                                    <span className="text-gray-700 truncate max-w-[160px]" title={s.store_name}>{s.store_name}</span>
                                  </span>
                                </td>
                                {valueCells(s[currency], s.late_lotes)}
                              </tr>
                              {storeOpen && <StoreDetail row={s} currency={currency} cutoffHour={cutoff} />}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Cómo se calcula */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-900 space-y-1">
            <p><strong>Cómo se calcula.</strong> Se parte de las ventas del día en Tecnodroid y se reparte cada método de pago:</p>
            <p>• <strong>Tarjetas:</strong> se usa solo la hora de cierre de cada lote en Tecnodroid. Cerrado antes de las {cutoffLabel} = ya en banco. Cerrado desde las {cutoffLabel} = cerrado tarde, cae un día después. Los lotes cerrados de madrugada (antes de las 8 am) son del turno nocturno: cuentan para el día anterior y como cerrados tarde.</p>
            <p>• <strong>Cashea:</strong> lo financiado menos 4% de comisión; queda por cobrar hasta que se registre el pago del ciclo en el módulo Cashea (se paga el miércoles siguiente al cierre lunes–domingo).</p>
            <p>• <strong>Pago móvil y Biopago:</strong> se cuentan como ya en banco.</p>
            <p>• <strong>Efectivo, divisas y crédito a clientes</strong> se muestran como referencia: no pasan por el banco.</p>
            <p>• <strong>Por aclarar</strong> = ventas − todo lo anterior. Casi siempre son ventas con tarjeta que no aparecen en ningún lote cerrado (lote no registrado o con monto mal cargado), o métodos de pago que Tecnodroid no clasifica.</p>
          </div>
        </>
      )}
    </div>
  );
}
